import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { useInjectSaga } from '../../utils/injectSaga';
import saga from './saga';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import { getLegalsAction, resetStateAction, getListLegalFileAction } from './actions';
import {
	getLegalsSelector,
	getIsLegalsLoadingSelector,
	getPaginationSelector,
	getIsDownloadLoadingSelector,
} from './selectors';
import { getIsAvailableLegalsButtonsSelector } from '../profile/selectors';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { TABLE_COLUMNS } from './constants';
import { normalizeDataGridModel } from '../../utils/normalizeDataGridModel';
import { DataGridComponent } from '../../components/data-grid';

const LegalsContainerWrapper = ({
	onGetLegals,
	legals,
	isLegalsLoading,
	onResetState,
	pagination,
	isAvailable,
	onGetListLegalFile,
	isDownloadLoading,
}) => {
	useInjectSaga({ key: 'legalsSaga', saga });
	useInjectReducer({ key: 'legalsReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	useEffect(() => {
		onGetLegals({ searchQuery: removeModalQuery(query.toString()) });

		return () => onResetState();
	}, []);

	const handleAddButtonClick = () => {
		query.set('modal', 'legal');
		query.set('mode', 'add');

		history.push(`${pathname}?${query}`);
	};

	const handlePageChange = (_, { activePage }) => {
		query.set('page', activePage);
		history.push(`${pathname}?${query}`);
		onGetLegals({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSelectChange = (_, { value }) => {
		query.set('page', 1);
		if (value === 'all') {
			query.delete('per_page');
			history.push(`${pathname}?${query}`);
		} else {
			query.set('per_page', value);
			history.push(`${pathname}?${query}`);
		}
		onGetLegals({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleDownloadButtonClick = () => {
		onGetListLegalFile({ exportUrl: '/api/legals/export' });
	};

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('sorts', jsonSorts);

		history.push(`${pathname}?${query}`);
		onGetLegals({ searchQuery: removeModalQuery(query.toString()) });
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			const jsonFilters = JSON.stringify(normalizeDataGridModel(filterModel, TABLE_COLUMNS()));

			query.delete('page');
			query.set('filters', jsonFilters);

			history.push(`${pathname}?${query}`);
			onGetLegals({ searchQuery: removeModalQuery(query.toString()) });
		}
	});

	return (
		<DataGridComponent
			headerText="Юр. лица"
			onAddButtonClick={isAvailable.create ? handleAddButtonClick : null}
			onDownloadButtonClick={isAvailable.export ? handleDownloadButtonClick : null}
			isDownloadButtonLoading={isDownloadLoading}
			columns={TABLE_COLUMNS()}
			rows={legals}
			isTableDataLoading={isLegalsLoading}
			pagination={
				!pagination
					? null
					: { ...pagination, onPageChange: handlePageChange, onSelectChange: handleSelectChange }
			}
			handleSortChange={handleSortChange}
			handleFilterChange={handleFilterChange}
		/>
	);
};

const mapStateToProps = createStructuredSelector({
	isLegalsLoading: getIsLegalsLoadingSelector(),
	legals: getLegalsSelector(),
	pagination: getPaginationSelector(),
	isAvailable: getIsAvailableLegalsButtonsSelector(),
	isDownloadLoading: getIsDownloadLoadingSelector(),
});

const mapDispatchToProps = {
	onGetLegals: getLegalsAction,
	onResetState: resetStateAction,
	onGetListLegalFile: getListLegalFileAction,
};

LegalsContainerWrapper.propTypes = {
	onGetListLegalFile: PropTypes.func.isRequired,
	onGetLegals: PropTypes.func.isRequired,
	isLegalsLoading: PropTypes.bool.isRequired,
	isDownloadLoading: PropTypes.bool.isRequired,
	legals: PropTypes.array.isRequired,
	onResetState: PropTypes.func.isRequired,
	pagination: PropTypes.shape({
		currentPage: PropTypes.number,
		totalPages: PropTypes.number.isRequired,
		total: PropTypes.number,
		from: PropTypes.number,
		to: PropTypes.number,
		isNextItem: PropTypes.bool,
		isPrevItem: PropTypes.bool,
		isFirstItem: PropTypes.bool,
		isLastItem: PropTypes.bool,
	}),
	isAvailable: PropTypes.objectOf(PropTypes.bool),
};

const LegalsContainer = connect(mapStateToProps, mapDispatchToProps)(LegalsContainerWrapper);

export default LegalsContainer;
